import { Directory, Filesystem } from '@capacitor/filesystem'

import type { ExerciseRun } from './exercise-runs'
import { isNative } from './platform'

/**
 * Disk-backed cache of the training-history list per exercise. Powers
 * the offline read path for
 * `resources/js/views/workspace/patient/TrainingHistory.vue`.
 *
 * Layout under `Directory.Data`:
 *
 *   lingodb/exercises/exercise-<id>-runs.json   — JSON array of ExerciseRun
 *
 * Co-located with the per-exercise media manifest
 * (`exercise-<id>.json`) so a "delete one exercise" sweep only has to
 * look in one directory. The two files are deliberately separate:
 * runs are derived data from the server, the manifest is local-only
 * download state. Mixing them would force a re-download every time a
 * new run uploaded.
 *
 * Persisted indefinitely — the user's directive is that the patient
 * app keeps working for weeks without connectivity. There's no
 * freshness window or TTL; the next online refresh overwrites the
 * cache, and `pruneTrainingHistory` deletes the file when its
 * exercise goes away.
 *
 * On web every helper is a no-op (return null / return early) — the
 * therapist surface runs in a browser without the Filesystem plugin
 * and has no offline requirement. Same convention as
 * `thumbnail-cache.ts` and `exercise-media-cache.ts`.
 */

const MANIFESTS_DIR = 'lingodb/exercises'

/**
 * Read the on-disk run list for one exercise. Returns null when the
 * file is missing / corrupt / running on web — never throws. The
 * shape is the verbatim server response; we don't try to migrate
 * older shapes here because the only thing that changes is which
 * columns the controller projects, and a stale row just means the
 * UI shows yesterday's data until the background refresh lands.
 */
export async function loadCachedRuns(exerciseId: number): Promise<ExerciseRun[] | null> {
  if (!isNative) return null
  try {
    const { data } = await Filesystem.readFile({
      path: runsPath(exerciseId),
      directory: Directory.Data,
      encoding: 'utf8' as never,
    })
    const text = typeof data === 'string' ? data : await (data as Blob).text()
    const parsed = JSON.parse(text)
    return Array.isArray(parsed) ? (parsed as ExerciseRun[]) : null
  } catch {
    return null
  }
}

export async function saveCachedRuns(exerciseId: number, runs: ExerciseRun[]): Promise<void> {
  if (!isNative) return
  await Filesystem.writeFile({
    path: runsPath(exerciseId),
    data: JSON.stringify(runs),
    directory: Directory.Data,
    encoding: 'utf8' as never,
    recursive: true,
  })
}

/**
 * Delete the cached run list for one exercise. Called from
 * `useExercisesStore.destroy()` alongside the media manifest sweep, so
 * a deleted exercise leaves no orphan files on disk. Tolerates a
 * missing file.
 */
export async function pruneTrainingHistory(exerciseId: number): Promise<void> {
  if (!isNative) return
  try {
    await Filesystem.deleteFile({
      path: runsPath(exerciseId),
      directory: Directory.Data,
    })
  } catch {
    // File never written or already gone — harmless.
  }
}

/**
 * Delete every cached run list on the device.
 *
 * Called after a device transfer completes: the run history belongs to the
 * account, and this device has just adopted a different one. Exercise ids are
 * per-patient integers, so a leftover `exercise-7-runs.json` would be read back
 * as the new account's exercise 7 — somebody else's results under the right
 * heading, which is worse than an empty list until the refetch lands.
 *
 * Only the run lists go: the sibling `exercise-<id>.json` media manifests
 * describe downloaded bytes, which are content and still valid.
 */
export async function clearTrainingHistory(): Promise<void> {
  if (!isNative) return
  try {
    const { files } = await Filesystem.readdir({
      path: MANIFESTS_DIR,
      directory: Directory.Data,
    })
    for (const file of files) {
      const name = typeof file === 'string' ? file : file.name
      if (!name.endsWith('-runs.json')) continue
      try {
        await Filesystem.deleteFile({
          path: `${MANIFESTS_DIR}/${name}`,
          directory: Directory.Data,
        })
      } catch {
        // Raced with a write, or already gone.
      }
    }
  } catch {
    // Directory never created — nothing cached, nothing to clear.
  }
}

function runsPath(exerciseId: number): string {
  return `${MANIFESTS_DIR}/exercise-${exerciseId}-runs.json`
}
